import { STATUS_CATEGORIES } from '@atlassian/jira-common-constants/src/status-categories.tsx';
import {
	type ApiReadViewWorkflowResponse,
	type ApiReadViewStatus,
	type ApiReadViewTransition,
	DIRECTED,
	GLOBAL,
	INITIAL,
} from './types.tsx';

export class WorkflowIntegrityError extends Error {}

const assertStatusesHaveValidNames = (statuses: ApiReadViewStatus[]) => {
	statuses.forEach((status) => {
		if (status.name.length === 0) {
			throw new WorkflowIntegrityError('Detected invalid empty status name.');
		}
	});
};

const assertStatusesHaveValidCategories = (statuses: ApiReadViewStatus[]) => {
	statuses.forEach((status) => {
		if (!STATUS_CATEGORIES.includes(status.statusCategory)) {
			throw new WorkflowIntegrityError(
				`Detected unexpected status category: ${status.statusCategory}.`,
			);
		}
	});
};

const assertTransitionsHaveValidNames = (transitions: ApiReadViewTransition[]) => {
	transitions.forEach((transition) => {
		if (transition.name.length === 0) {
			throw new WorkflowIntegrityError('Detected invalid empty transition name.');
		}
	});
};

const assertInitialTransitionExists = (transitions: ApiReadViewTransition[]) => {
	const countInitialTransitions = transitions.filter((t) => t.type === INITIAL).length;
	if (countInitialTransitions !== 1) {
		throw new WorkflowIntegrityError(
			`Detected workflow with ${countInitialTransitions} INITIAL transitions. Expected a single one.`,
		);
	}
};

const assertTransitionsAreCorrectlyConnected = (
	transitions: ApiReadViewTransition[],
	statuses: ApiReadViewStatus[],
) => {
	const validStatusReferences = statuses.map((status) => status.statusReference);

	transitions
		.filter((t) => t.type === DIRECTED)
		.forEach((transition) => {
			const {
				toStatusReference,
				links: [{ fromStatusReference }],
			} = transition;

			// DIRECTED transitions have to have valid `from` and `to` references
			if (
				!fromStatusReference ||
				!validStatusReferences.includes(fromStatusReference) ||
				!toStatusReference ||
				!validStatusReferences.includes(toStatusReference)
			) {
				throw new WorkflowIntegrityError(
					'Detected DIRECTED transition with invalid from and to references (either or both).',
				);
			}
		});

	transitions
		.filter((t) => t.type === INITIAL)
		.forEach((transition) => {
			const { toStatusReference, links } = transition;

			const fromRefs = links
				.map((link) => link.fromStatusReference)
				.filter((ref) => ref !== undefined);

			// INITIAL transitions only need to have a valid `to` reference
			if (
				!toStatusReference ||
				!validStatusReferences.includes(toStatusReference) ||
				fromRefs.length
			) {
				throw new WorkflowIntegrityError(
					`Detected ${transition.type} transition without a valid destination or with a defined "from".`,
				);
			}
		});

	const unknownType = transitions.find(
		(t) => t.type !== DIRECTED && t.type !== GLOBAL && t.type !== INITIAL,
	);
	if (unknownType) {
		throw new WorkflowIntegrityError(`Detected unexpected transition type: ${unknownType.type}.`);
	}
};

/**
 This method aims to validate the document from a LOGICAL standpoint.
 Assumptions regarding shape and general types are covered with pact testing.
 */
export const validateLogicalDocumentIntegrity = (result: ApiReadViewWorkflowResponse) => {
	const { statuses } = result;
	const workFlowTransitions = result.workflows.map((workflow) => workflow.transitions);

	assertStatusesHaveValidNames(statuses);
	assertStatusesHaveValidCategories(statuses);

	workFlowTransitions.forEach((transitions) => {
		assertTransitionsHaveValidNames(transitions);
		assertTransitionsAreCorrectlyConnected(transitions, statuses);
		assertInitialTransitionExists(transitions);
	});
	return result;
};
